import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import {
  ActionPerformed,
  PermissionStatus,
  PushNotifications,
  PushNotificationSchema,
  RegistrationError,
  Token,
} from '@capacitor/push-notifications';
import { firstValueFrom, Subject } from 'rxjs';

import { DebugHelper } from '@shared/helpers/debug.helper';
import { StorageService } from '@shared/services/storage.service';
import { ToastService } from '@shared/services/toast.service';
import { RoutingService } from '@shared/services/routing.service';
import { UserApiService } from '@user/services/user-api.service';

@Injectable({ providedIn: 'root' })
export class PushNotificationService {

  private _userApiService = inject(UserApiService);
  private _storageService = inject(StorageService);
  private _router = inject(Router);
  private _toastService = inject(ToastService);

  readonly notificationReceived$ = new Subject<void>();

  constructor() {
    void this._registerListeners();
  }

  static isAvailable(): boolean {
    return Capacitor.isPluginAvailable('PushNotifications');
  }

  async register(): Promise<boolean> {
    const preference = await this._storageService.getPushNotificationsEnabled();
    if (preference === false) {
      return false;
    }

    if (!PushNotificationService.isAvailable()) {
      return false;
    }

    const status: PermissionStatus = await PushNotifications.requestPermissions();
    if (status.receive !== 'granted') {
      return false;
    }

    await PushNotifications.register();
    return true;
  }

  async unregister(): Promise<void> {
    const storedToken = await this._storageService.getFcmToken();
    if (!storedToken) {
      return;
    }

    if (PushNotificationService.isAvailable()) {
      try {
        await firstValueFrom(this._userApiService.deleteFcm$());
      } catch (error) {
        DebugHelper.error(`Failed to delete FCM token from backend: ${ JSON.stringify(
            error) }`);
      }
    }

    await this._storageService.removeFcmToken();
  }

  private async _registerListeners(): Promise<void> {
    if (!PushNotificationService.isAvailable()) {
      return;
    }

    await PushNotifications.addListener('registration', (token: Token) => {
      if (!token.value) {
        DebugHelper.log('Empty FCM token received');
        return;
      }
      DebugHelper.log(`FCM registration success: ${ token.value }`);
      void this._onTokenReceived(token.value);
    });

    await PushNotifications.addListener(
        'registrationError',
        (error: RegistrationError) => {
          DebugHelper.error(`FCM registration error: ${ JSON.stringify(error) }`);
        },
    );

    await PushNotifications.addListener(
        'pushNotificationReceived',
        (notification: PushNotificationSchema) => {
          DebugHelper.log(`Push received: ${ JSON.stringify(notification) }`);
          void this._onNotificationReceived(notification);
        },
    );

    await PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (_action: ActionPerformed) => {
          DebugHelper.log('Push action performed');
          this._onNotificationTapped();
        },
    );
  }

  private async _onTokenReceived(token: string): Promise<void> {
    const preference = await this._storageService.getPushNotificationsEnabled();
    if (preference === false) {
      return;
    }

    try {
      await firstValueFrom(this._userApiService.registerFcm$({ fcm: token }));
      await this._storageService.setFcmToken(token);
      DebugHelper.log('FCM token registered and stored');
    } catch (error) {
      DebugHelper.error(`Failed to register FCM token: ${ JSON.stringify(error) }`);
    }
  }

  private async _onNotificationReceived(notification: PushNotificationSchema): Promise<void> {
    const title = notification.title ?? 'Nova notificação';
    const body = notification.body ?? '';
    const message = body ? `${ title }: ${ body }` : title;
    await this._toastService.success(message);
    this.notificationReceived$.next();
  }

  private _onNotificationTapped(): void {
    void this._router.navigateByUrl(RoutingService.NOTIFICATIONS);
  }
}
